import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';

import {
  abilityGroups,
  buildQuery,
  buildSummary,
  resolveBuild,
  sanitizeBuild,
  STORAGE_KEY,
  type BuildState,
  type GroupKey,
} from '@/lib/build-planner';

export interface PlannerLabels {
  clear: string;
  reset: string;
  copy: string;
  copied: string;
  summary: string;
  empty: string;
  selected: string;
  fixed: string;
  command: string;
  storage: string;
  manual: string;
  link: string;
  tags: string;
}
export function BuildPlanner({
  labels,
  names = {},
}: {
  labels: PlannerLabels;
  names?: Record<string, string>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState<BuildState>(() =>
    sanitizeBuild(location.search)
  );
  const [ready, setReady] = useState(false);
  const [storageError, setStorageError] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [manualLink, setManualLink] = useState('');
  useEffect(() => {
    let saved: unknown;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      saved = raw ? JSON.parse(raw) : {};
    } catch {
      setStorageError(true);
    }
    const next = resolveBuild(
      location.search as Record<string, unknown>,
      saved
    );
    setState(next);
    setReady(true);
  }, [location.searchStr]);
  async function update(next: BuildState, reset = false) {
    setState(next);
    setFeedback('');
    setManualLink('');
    try {
      if (reset) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      setStorageError(true);
    }
    await navigate({ to: '/build-planner', search: next, replace: true });
  }
  function clear(key: GroupKey) {
    const next = { ...state };
    delete next[key];
    void update(next);
  }
  async function share() {
    const query = buildQuery(state);
    const url =
      window.location.origin +
      window.location.pathname +
      (query ? '?' + query : '');
    try {
      await navigator.clipboard.writeText(url);
      setFeedback(labels.copied);
      setManualLink('');
    } catch {
      setFeedback(labels.manual);
      setManualLink(url);
    }
  }
  const summary = buildSummary(state);
  return (
    <section className="crt-planner" aria-label={labels.summary}>
      <div className="crt-grid crt-grid-three">
        {abilityGroups.map((group) => (
          <fieldset className="crt-choice-group" key={group.key}>
            <legend>{names[group.name] || group.name}</legend>
            <div className="crt-choice-options">
              {group.options.map((option) => (
                <label
                  className={
                    state[group.key] === option.id
                      ? 'crt-choice crt-selected'
                      : 'crt-choice'
                  }
                  key={option.id}
                >
                  <input
                    type="radio"
                    name={group.key}
                    value={option.id}
                    checked={state[group.key] === option.id}
                    disabled={!ready}
                    onChange={() =>
                      void update({ ...state, [group.key]: option.id })
                    }
                  />
                  <span>{names[option.name] || option.name}</span>
                  {state[group.key] === option.id && (
                    <span className="crt-selection-label">
                      ✓ {labels.selected}
                    </span>
                  )}
                </label>
              ))}
            </div>
            <button
              type="button"
              className="crt-clear"
              disabled={!ready || !state[group.key]}
              onClick={() => clear(group.key)}
              aria-label={
                labels.clear + ' ' + (names[group.name] || group.name)
              }
            >
              {labels.clear}
            </button>
          </fieldset>
        ))}
        <div className="crt-choice-group">
          <h2>{labels.fixed}</h2>
          <p className="crt-fixed-command">{names.Command || 'Command'}</p>
          <p className="crt-muted">{labels.command}</p>
        </div>
      </div>
      <div className="crt-build-summary">
        <h2>{labels.summary}</h2>
        {summary.length ? (
          <ul>
            {summary.map((item) => (
              <li key={names[item.group] || item.group}>
                <span>{names[item.group] || item.group}</span>
                <strong>{names[item.name] || item.name}</strong>
              </li>
            ))}
          </ul>
        ) : (
          <p className="crt-muted">{labels.empty}</p>
        )}
        <p className="crt-muted">{labels.tags}</p>
        <div className="crt-tags">
          {[...new Set(summary.map((item) => item.tag))].map((tag) => (
            <span key={names[tag] || tag}>{names[tag] || tag}</span>
          ))}
        </div>
      </div>
      <div className="crt-actions">
        <button
          type="button"
          className="crt-button"
          disabled={!ready}
          onClick={() => void share()}
        >
          {labels.copy}
        </button>
        <button
          type="button"
          className="crt-button crt-button-secondary"
          disabled={!ready}
          onClick={() => void update({}, true)}
        >
          {labels.reset}
        </button>
      </div>
      <p role="status" className="crt-muted">
        {feedback}
      </p>
      {manualLink && (
        <label className="crt-manual-link">
          {labels.link}
          <input
            readOnly
            value={manualLink}
            onFocus={(event) => event.currentTarget.select()}
          />
        </label>
      )}
      {storageError && (
        <p role="status" className="crt-muted">
          {labels.storage}
        </p>
      )}
    </section>
  );
}
